import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTypewriterOptions {
  text: string;
  delay?: number;
  startDelay?: number;
  onComplete?: () => void;
}

export const useTypewriter = ({
  text,
  delay = 50,
  startDelay = 500,
  onComplete
}: UseTypewriterOptions) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const indexRef = useRef(0);

  const typeNextCharacter = useCallback(() => {
    if (indexRef.current < text.length) {
      setDisplayText(prev => prev + text.charAt(indexRef.current));
      indexRef.current++;
      
      timeoutRef.current = setTimeout(() => {
        typeNextCharacter();
      }, delay);
    } else {
      setIsTyping(false);
      if (onComplete) {
        onComplete();
      }
    }
  }, [text, delay, onComplete]);

  useEffect(() => {
    // Reset state when text changes
    setDisplayText('');
    setIsTyping(false);
    indexRef.current = 0;

    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!text) return;

    // Start typing after initial delay
    timeoutRef.current = setTimeout(() => {
      setIsTyping(true);
      typeNextCharacter();
    }, startDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, startDelay, typeNextCharacter]);

  return { displayText, isTyping };
};